// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useEffect, useState } from 'react';

import { extractErrorMessage } from '@evolvemep/api-ts';
import { Checkbox, Flex, Input, LoadingOverlay, Text } from '@mantine/core';
// oxlint-disable-next-line no-restricted-imports
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { EULAModal } from 'app/EULAModal';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { isNotNil } from 'helpers/isNotNil';
import { useMaybeUser } from 'hooks/user/useMaybeUser';

import { useConfirmUser } from '../auth/restCalls';
import { useSignIn } from '../auth/useSignIn';
import { type SignInFormValues, useSignInSteps } from '../auth/useSignInSteps';
import { AuthFormWrapper } from '../components/AuthFormWrapper';
import { EmailStepFields } from '../components/EmailStepFields';
import type { LoginErrorCode } from '../components/LoginErrorAlert';
import { PasswordStepFields } from '../components/PasswordStepFields';

export const Login = () => {
  const { confirmUser } = useConfirmUser();
  const { user: existingUser, refreshUser, loading: loadingUser } = useMaybeUser();
  const { signIn } = useSignIn();
  const [signingIn, setSigningIn] = useState(false);
  const [keepEnabled, setKeepEnabled] = useState(false);

  const [isConfirmed, setIsConfirmed] = useState(false);
  // useEvolveNavigate only works inside Shop routes
  const navigate = useNavigate();
  const {
    // From src/modules/Authentication/ResetPassword.tsx
    resetPassword: changePasswordSuccess,
    // From src/modules/Authentication/Join/Join.tsx
    inviteConfirm: confirmationSuccess,
    // src/modules/Authentication/AcceptInvitation.tsx
    joinConfirm: joinSuccess,
  } = (useLocation().state ?? {}) as {
    resetPassword?: boolean;
    inviteConfirm?: boolean;
    joinConfirm?: string;
  };

  const [query] = useSearchParams();
  const queryErrorCode = query.get('errorCode') as LoginErrorCode | null;
  const username = query.get('username');
  const email = query.get('email');
  const code = query.get('code');
  const redirect = query.get('redirect') ?? '/';
  const [errorCode, setErrorCode] = useState<LoginErrorCode | string | undefined>(queryErrorCode ?? undefined);
  const { form, showPasswordStep, submittingEmail, submitEmail, goToEmail, stepBoxProps } = useSignInSteps({
    initialEmail: email ?? undefined,
    ssoState: { previousLoc: redirect },
  });

  useEffect(() => {
    if (errorCode) setSigningIn(false);
  }, [errorCode]);

  const [successMessage, setSuccessMessage] = useState<string>();

  useEffect(() => {
    if (joinSuccess) {
      setSuccessMessage(`You have joined ${joinSuccess}! Login to continue.`);
    }
  }, [joinSuccess]);
  useEffect(() => {
    if (isConfirmed) {
      setSuccessMessage('Your email address has been verified!');
    }
  }, [isConfirmed]);
  useEffect(() => {
    if (changePasswordSuccess) {
      setSuccessMessage('Success! Your new password has been set.');
    }
  }, [changePasswordSuccess]);
  useEffect(() => {
    if (confirmationSuccess) {
      setSuccessMessage('Success! You have successfully joined.');
    }
  }, [confirmationSuccess]);

  useEffect(() => {
    if (!isConfirmed && username && code) {
      confirmUser(username, 'company', { confirmationCode: code })
        .then(() => setIsConfirmed(true))
        .catch(() => setIsConfirmed(false));
    }
  }, [code, confirmUser, isConfirmed, username]);

  const handleSignIn = async (values: SignInFormValues) => {
    setErrorCode(undefined);
    setSuccessMessage(undefined);
    setSigningIn(true);

    try {
      await signIn({ ...values, keepEnabled });
      const user = await refreshUser();
      if (!user) {
        setErrorCode('UnknownException');
      } else if (user.acceptedLatestEula) {
        void navigate(redirect);
      }
    } catch (e) {
      setErrorCode(extractErrorMessage(e));
    }
  };

  const checkUserAuthenticationUrl = async (values: SignInFormValues) => {
    setErrorCode(undefined);
    setErrorCode(await submitEmail(values));
  };

  if (showPasswordStep) {
    return (
      <AuthFormWrapper
        title="Login"
        form={form}
        onSubmit={handleSignIn}
        errorCode={errorCode}
        onCloseError={() => setErrorCode(undefined)}
        successMessage={successMessage}
        onCloseSuccess={() => setSuccessMessage(undefined)}
        boxProps={stepBoxProps}
      >
        <PasswordStepFields
          form={form}
          busy={signingIn}
          submitLabel="Login"
          onChangeLogin={goToEmail}
          options={
            <Checkbox
              color="dark"
              size="md"
              label={
                <Flex align="center" gap={6}>
                  <Text>Keep me signed in</Text>
                  <InfoIcon label="Leave this unchecked if this is a shared device." />
                </Flex>
              }
              disabled={signingIn}
              checked={keepEnabled}
              onChange={(event) => setKeepEnabled(event.currentTarget.checked)}
            />
          }
        />
        <EULAModal onEulaAccepted={() => navigate(redirect)} />
      </AuthFormWrapper>
    );
  }

  if (isNotNil(existingUser)) {
    return (
      <AuthFormWrapper title="" form={form} onSubmit={handleSignIn}>
        <Input.Label mb={-10} size="md">
          Email
        </Input.Label>
        <Text c="gray.9">{existingUser.userEmail}</Text>
        <EULAModal onEulaAccepted={() => navigate(redirect)} />
      </AuthFormWrapper>
    );
  }

  return (
    <AuthFormWrapper
      title="Login"
      form={form}
      onSubmit={checkUserAuthenticationUrl}
      errorCode={errorCode}
      onCloseError={() => setErrorCode(undefined)}
      successMessage={successMessage}
      onCloseSuccess={() => setSuccessMessage(undefined)}
      boxProps={stepBoxProps}
    >
      <EmailStepFields form={form} busy={submittingEmail} />
      <LoadingOverlay visible={loadingUser} />
    </AuthFormWrapper>
  );
};
